/**
 * Split Bill Modal for POS
 * Allows cashier to select individual items from a table's bill for partial payment
 * Supports multiple people paying for their own items
 */
import { useState, useEffect } from 'react';
import { 
  X, Check, Users, CreditCard, Banknote, Smartphone,
  Loader2, Receipt, ChevronRight, Minus, Plus, AlertCircle,
  CheckCircle, RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import api from '../../utils/apiClient';

const PAYMENT_METHODS = [
  { id: 'cash', name: 'Cash', icon: Banknote, color: 'bg-green-500' },
  { id: 'card', name: 'Card', icon: CreditCard, color: 'bg-blue-500' },
  { id: 'eftpos', name: 'EFTPOS', icon: CreditCard, color: 'bg-purple-500' },
  { id: 'mpaisa_qr', name: 'M-PAiSA', icon: Smartphone, color: 'bg-orange-500' }
];

export default function SplitBillModal({
  isOpen,
  onClose,
  tableNumber,
  staffId,
  staffName,
  onPaymentComplete
}) {
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [items, setItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]); // Array of {order_id, item_index, unit_index}
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [customerName, setCustomerName] = useState('');
  const [mpaisaRef, setMpaisaRef] = useState('');
  const [billTotal, setBillTotal] = useState(0);
  const [remainingAfterSplit, setRemainingAfterSplit] = useState([]);
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);
  const [lastPaymentResult, setLastPaymentResult] = useState(null);

  // Fetch items when modal opens
  useEffect(() => {
    if (isOpen && tableNumber) {
      fetchTableItems();
    }
  }, [isOpen, tableNumber]);

  const fetchTableItems = async () => {
    setLoading(true);
    setSelectedItems([]);
    setPaymentMethod(null);
    setCustomerName('');
    setShowPaymentSuccess(false);
    
    try {
      const res = await api.get(`/api/pos/table-bill/${tableNumber}/items`, {
        headers: { 'X-Staff-ID': staffId }
      });
      
      if (res.data.found) {
        setItems(res.data.items);
        setBillTotal(res.data.total);  // Total is now VAT-inclusive
      } else {
        setItems([]);
        setBillTotal(0);
        toast.info(`Table ${tableNumber} has no unpaid items`);
      }
    } catch (err) {
      console.error('Failed to fetch table items:', err);
      toast.error('Failed to load table bill');
    } finally {
      setLoading(false);
    }
  };

  // Calculate selected total (prices are VAT-inclusive)
  const selectedTotal = selectedItems.reduce((sum, sel) => {
    const item = items.find(
      i => i.order_id === sel.order_id && 
           i.item_index === sel.item_index && 
           i.unit_index === sel.unit_index
    );
    return sum + (item?.price || 0);
  }, 0);

  // Extract VAT from inclusive total (VAT = total - total/1.125)
  const selectedSubtotal = selectedTotal / 1.125;
  const selectedVat = selectedTotal - selectedSubtotal;
  // Grand total is the VAT-inclusive price (no additional VAT needed)
  const selectedGrandTotal = selectedTotal;

  // Toggle item selection
  const toggleItemSelection = (item) => {
    const key = `${item.order_id}-${item.item_index}-${item.unit_index}`;
    const isSelected = selectedItems.some(
      sel => sel.order_id === item.order_id && 
             sel.item_index === item.item_index && 
             sel.unit_index === item.unit_index
    );

    if (isSelected) {
      setSelectedItems(prev => prev.filter(
        sel => !(sel.order_id === item.order_id && 
                 sel.item_index === item.item_index && 
                 sel.unit_index === item.unit_index)
      ));
    } else {
      setSelectedItems(prev => [...prev, {
        order_id: item.order_id,
        item_index: item.item_index,
        unit_index: item.unit_index,
        quantity: 1
      }]);
    }
  };

  // Select all items
  const selectAllItems = () => {
    const allSelections = items.map(item => ({
      order_id: item.order_id,
      item_index: item.item_index,
      unit_index: item.unit_index,
      quantity: 1
    }));
    setSelectedItems(allSelections);
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedItems([]);
  };

  // Check if item is selected
  const isItemSelected = (item) => {
    return selectedItems.some(
      sel => sel.order_id === item.order_id && 
             sel.item_index === item.item_index && 
             sel.unit_index === item.unit_index
    );
  };

  // Process split payment
  const handlePayment = async () => {
    if (selectedItems.length === 0) {
      toast.error('Please select items to pay');
      return;
    }
    
    if (!paymentMethod) {
      toast.error('Please select a payment method');
      return;
    }

    if (paymentMethod === 'mpaisa_qr' && !mpaisaRef) {
      toast.error('Please enter the M-PAiSA reference code');
      return;
    }

    setProcessing(true);
    
    try {
      // Group selected items by order_id and item_index for the API
      const itemsPayload = [];
      const itemMap = new Map();
      
      for (const sel of selectedItems) {
        const key = `${sel.order_id}-${sel.item_index}`;
        if (itemMap.has(key)) {
          itemMap.get(key).quantity += 1;
        } else {
          itemMap.set(key, {
            order_id: sel.order_id,
            item_index: sel.item_index,
            quantity: 1
          });
        }
      }
      
      for (const item of itemMap.values()) {
        itemsPayload.push(item);
      }

      const res = await api.post('/api/pos/split-bill/pay', {
        table_number: tableNumber,
        selected_items: itemsPayload,
        payment_method: paymentMethod,
        staff_id: staffId,
        staff_name: staffName,
        customer_name: customerName || undefined,
        mpaisa_ref_code: paymentMethod === 'mpaisa_qr' ? mpaisaRef : undefined
      }, {
        headers: { 'X-Staff-ID': staffId }
      });

      if (res.data.success) {
        setLastPaymentResult(res.data);
        setShowPaymentSuccess(true);
        
        toast.success(`Payment of $${res.data.amount_paid.toFixed(2)} processed!`, {
          description: res.data.table_fully_paid 
            ? `Table ${tableNumber} fully paid!` 
            : 'Remaining items still on bill',
          icon: <CheckCircle className="w-4 h-4" />
        });

        // If table is fully paid, close modal
        if (res.data.table_fully_paid) {
          setTimeout(() => {
            onPaymentComplete?.(res.data);
            onClose();
          }, 2000);
        } else {
          // Refresh items to show remaining
          setTimeout(() => {
            fetchTableItems();
            setShowPaymentSuccess(false);
          }, 1500);
        }
      }
    } catch (err) {
      console.error('Split payment failed:', err);
      toast.error(err.response?.data?.detail || 'Payment failed');
    } finally {
      setProcessing(false);
    }
  };

  // Group items by name for cleaner display
  const groupedItems = items.reduce((acc, item) => {
    const key = `${item.order_id}-${item.item_index}`;
    if (!acc[key]) {
      acc[key] = {
        ...item,
        units: []
      };
    }
    acc[key].units.push(item);
    return acc;
  }, {});

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-indigo-500 flex items-center justify-center">
              <Users className="w-5 h-5 text-white" />
            </div>
            <div>
              <span>Split Bill - Table {tableNumber}</span>
              {items.length > 0 && (
                <p className="text-sm font-normal text-gray-500 dark:text-gray-400">
                  {items.length} item{items.length !== 1 ? 's' : ''} • Total: ${billTotal.toFixed(2)}
                </p>
              )}
            </div>
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex-1 flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
          </div>
        ) : showPaymentSuccess ? (
          <div className="flex-1 flex flex-col items-center justify-center py-8">
            <div className="w-20 h-20 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-4">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
              Payment Successful!
            </h3>
            <p className="text-lg text-green-600 font-semibold mb-1">
              ${lastPaymentResult?.amount_paid?.toFixed(2)} paid
            </p>
            <p className="text-gray-500 dark:text-gray-400">
              {lastPaymentResult?.table_fully_paid 
                ? 'Table fully paid - closing...' 
                : 'Loading remaining items...'}
            </p>
          </div>
        ) : items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center py-12">
            <CheckCircle className="w-16 h-16 text-green-500 mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Table {tableNumber} Fully Paid!
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mb-4">
              All items have been paid for.
            </p>
            <Button onClick={onClose} variant="outline">
              Close
            </Button>
          </div>
        ) : (
          <div className="flex-1 flex gap-4 overflow-hidden">
            {/* Left: Item Selection */}
            <div className="flex-1 flex flex-col overflow-hidden">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-gray-700 dark:text-gray-300">
                  Select Items to Pay
                </h4>
                <div className="flex gap-2">
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={clearSelection}
                    disabled={selectedItems.length === 0}
                  >
                    Clear
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={selectAllItems}
                    disabled={selectedItems.length === items.length}
                  >
                    Select All
                  </Button>
                </div>
              </div>
              
              <div className="flex-1 overflow-y-auto space-y-2 pr-2" data-testid="split-bill-items">
                {Object.values(groupedItems).map((group) => (
                  <div key={`${group.order_id}-${group.item_index}`} className="space-y-1">
                    {group.units.map((item, unitIdx) => {
                      const selected = isItemSelected(item);
                      return (
                        <button
                          key={`${item.order_id}-${item.item_index}-${item.unit_index}`}
                          onClick={() => toggleItemSelection(item)}
                          className={`w-full p-3 rounded-lg border-2 transition-all text-left ${
                            selected
                              ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/30'
                              : 'border-gray-200 dark:border-gray-700 hover:border-indigo-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                          }`}
                          data-testid={`split-item-${item.item_index}-${unitIdx}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                                selected 
                                  ? 'border-indigo-500 bg-indigo-500' 
                                  : 'border-gray-300 dark:border-gray-600'
                              }`}>
                                {selected && <Check className="w-4 h-4 text-white" />}
                              </div>
                              <div>
                                <p className="font-medium text-gray-900 dark:text-white">
                                  {item.name}
                                  {group.units.length > 1 && (
                                    <span className="text-gray-500 ml-1">
                                      ({unitIdx + 1}/{group.units.length})
                                    </span>
                                  )}
                                </p>
                                {item.size && (
                                  <p className="text-xs text-gray-500">
                                    {typeof item.size === 'object' ? item.size.name : item.size}
                                  </p>
                                )}
                              </div>
                            </div>
                            <span className={`font-bold ${
                              selected ? 'text-indigo-600' : 'text-gray-700 dark:text-gray-300'
                            }`}>
                              ${item.price.toFixed(2)}
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>

            {/* Right: Payment Panel */}
            <div className="w-80 flex flex-col border-l border-gray-200 dark:border-gray-700 pl-4">
              {/* Selected Summary */}
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 mb-4">
                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
                  Selected Items: {selectedItems.length}
                </h4>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between text-gray-600 dark:text-gray-400">
                    <span>Subtotal (excl. VAT)</span>
                    <span>${selectedSubtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-gray-600 dark:text-gray-400">
                    <span>VAT (12.5%)</span>
                    <span>${selectedVat.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold text-gray-900 dark:text-white pt-2 border-t border-gray-200 dark:border-gray-700">
                    <span>Total</span>
                    <span>${selectedGrandTotal.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Customer Name (Optional) */}
              <div className="mb-4">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">
                  Customer Name (optional)
                </label>
                <Input
                  placeholder="e.g., John"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  className="h-10"
                />
              </div>

              {/* Payment Method */}
              <div className="mb-4">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                  Payment Method
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {PAYMENT_METHODS.map((method) => {
                    const Icon = method.icon;
                    return (
                      <button
                        key={method.id}
                        onClick={() => setPaymentMethod(method.id)}
                        className={`p-3 rounded-lg border-2 transition-all ${
                          paymentMethod === method.id
                            ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/30'
                            : 'border-gray-200 dark:border-gray-700 hover:border-indigo-300'
                        }`}
                        data-testid={`split-payment-${method.id}`}
                      >
                        <Icon className={`w-5 h-5 mx-auto mb-1 ${
                          paymentMethod === method.id ? 'text-indigo-600' : 'text-gray-500'
                        }`} />
                        <span className={`text-xs font-medium ${
                          paymentMethod === method.id ? 'text-indigo-600' : 'text-gray-600 dark:text-gray-400'
                        }`}>
                          {method.name}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* M-PAiSA Reference */}
              {paymentMethod === 'mpaisa_qr' && (
                <div className="mb-4">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">
                    M-PAiSA Reference Code
                  </label>
                  <Input
                    placeholder="Enter ref code"
                    value={mpaisaRef}
                    onChange={(e) => setMpaisaRef(e.target.value)}
                    className="h-10"
                  />
                </div>
              )}

              {/* Pay Button */}
              <Button
                onClick={handlePayment}
                disabled={processing || selectedItems.length === 0 || !paymentMethod}
                className="w-full h-12 bg-indigo-600 hover:bg-indigo-700 text-white mt-auto"
                data-testid="split-pay-btn"
              >
                {processing ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Receipt className="w-5 h-5 mr-2" />
                    Pay ${selectedGrandTotal.toFixed(2)}
                  </>
                )}
              </Button>

              {/* Remaining Info */}
              {selectedItems.length > 0 && selectedItems.length < items.length && (
                <div className="mt-3 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                  <p className="text-xs text-amber-700 dark:text-amber-400">
                    <AlertCircle className="w-3 h-3 inline mr-1" />
                    {items.length - selectedItems.length} item{items.length - selectedItems.length !== 1 ? 's' : ''} will remain on the bill
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
